"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import { Container, SectionTitle } from "./Primitives";

type ServiceCard = {
  key: string;
  title: string;
  body: string;
  image: string;
};

/**
 * Четыре услуги. CMS-ready: при подключении заменим на fetch из
 * `/api/v1/company/services`. У каждой услуги своя картинка справа.
 */
// TODO: при подключении CMS каждая услуга получит свою картинку нужного
// формата (vertical 3:4, ≥640px по высоте). Пока — одна и та же vertical-
// фотография для всех 4 услуг, чтобы масштаб и crop были единообразны при
// переключении (object-cover не «прыгает» между разными aspect-ratio).
const SHARED_IMAGE = "/img/tagjs/kjttuxt5.png";

const SERVICES: ServiceCard[] = [
  {
    key: "delegations",
    title: "Профессиональное транспортное обслуживание официальных лиц и делегаций",
    body:
      "С каждым годом возрастают масштабы экономических и политических событий страны, пропорционально растет и объем требуемых транспортных услуг, в том числе представителям иностранных государств, международного и российского бизнеса. Для комфортных поездок требуется транспортное сопровождение. Компания «Автопилот» готова его предоставить на высшем уровне.",
    image: SHARED_IMAGE,
  },
  {
    key: "events",
    title: "Транспортное обеспечение крупнейших международных мероприятий и спортивных событий",
    body:
      "Мы разработали и реализовали проекты по транспортной логистике крупнейших мероприятий мирового значения, таких как Саммит АТЭС, Олимпиада в Сочи, Всемирный фестиваль молодежи и студентов, Чемпионат мира по футболу, экономические форумы (РИФ, ПМЭФ, ВЭФ), форумы и конгрессы (Россия-Африка, АСЕАН, ШОС, БРИКС), мероприятия (Таврида-АРТ, WorldSkills, открытие Крымского моста).",
    image: SHARED_IMAGE,
  },
  {
    key: "rental",
    title: "Прокат автомобилей без водителя",
    body:
      "Станции проката «Автопилот» расположены в 11 регионах России. Автопарк компании включает более 1000 автомобилей, и мы являемся крупнейшим сервисом по прокату авто в стране. Кроссоверы, минивэны, авто класса люкс и комфорт — для любых задач и любых маршрутов. Поддержка 24/7, прозрачный договор, пробег без ограничений.",
    image: SHARED_IMAGE,
  },
  {
    key: "transfers",
    title: "Корпоративный транспорт и трансферы",
    body:
      "Регулярные перевозки сотрудников, встречи и проводы делегаций, аэропорт-трансферы для VIP-клиентов. Современный парк бизнес-седанов и минивэнов с водителями международного класса, опыт работы с дипломатическими протоколами и крупными корпоративными заказчиками.",
    image: SHARED_IMAGE,
  },
];

/**
 * Services — двухколоночный блок:
 *  • LEFT (1fr): 4 текстовые строки. Активная — синяя `bg-primary` с
 *    description, ВЫЛЕЗАЕТ ВПРАВО и уходит под картинку. Неактивные —
 *    БЕЗ фона, только текст на сером page-background.
 *  • RIGHT (минфикс): большая картинка fill, z-выше — перекрывает правый
 *    край активной синей карточки.
 *
 * Мобильно: картинка сверху, аккордеон под ней.
 */
export default function Services() {
  const [activeKey, setActiveKey] = useState(SERVICES[0].key);
  // previousKey — какая картинка УХОДИТ. Остаётся видимой как нижний слой
  // пока новая активная "заслоняет" её через fade-in. После окончания
  // анимации (250ms — чуть больше transition 200ms) previousKey очищается.
  const [previousKey, setPreviousKey] = useState<string | null>(null);
  const prevTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function selectService(key: string) {
    if (key === activeKey) return;
    if (prevTimerRef.current) clearTimeout(prevTimerRef.current);
    setPreviousKey(activeKey);
    setActiveKey(key);
    prevTimerRef.current = setTimeout(() => setPreviousKey(null), 250);
  }

  return (
    <section
      id="services"
      className="pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]"
    >
      <Container>
        <SectionTitle className="mb-8 sm:mb-10 lg:mb-[50px]">Наши услуги</SectionTitle>

        {/* lg:gap-8 — общий зазор между колонкой карточек и картинкой.
            Заменяет индивидуальный mr у неактивных — даёт ровный одинаковый
            отступ для всех текстовых блоков от картинки справа. */}
        {/* items-start — картинка имеет ФИКСИРОВАННУЮ высоту и не тянется
            под высоту колонки текста. min-h-[640px] резервирует место под
            максимальный body (самое длинное описание) + 4 свёрнутые карточки,
            чтобы вся секция НЕ МЕНЯЛА свою высоту при переключении услуг
            (иначе соседние блоки и картинка визуально сдвигаются). */}
        <div className="relative grid gap-5 lg:grid-cols-[1fr_minmax(360px,460px)] lg:gap-8 xl:gap-10 lg:items-start lg:min-h-[640px] xl:min-h-[660px]">
          {/* LEFT: text list. isolate создаёт scoped stacking context —
              любые внутренние z-эффекты (включая стек-контексты от transform
              анимаций) ограничены в этом контейнере и не могут «пробить» z-10
              картинки. Это убирает ситуацию, когда синий блок ВО ВРЕМЯ
              анимации временно выше фото. */}
          <ul className="relative isolate z-[1] flex flex-col gap-2 sm:gap-3 lg:order-1">
            {SERVICES.map((s) => {
              const isActive = s.key === activeKey;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => selectService(s.key)}
                    aria-expanded={isActive}
                    // Transition применяется ТОЛЬКО к активной (enter-only).
                    // Старая активная при переключении мгновенно становится
                    // неактивной (без transition-класса) — никакого fade-out.
                    // Только новая активная плавно «вырастает» из inactive-
                    // state (background, width, shadow).
                    style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
                    className={`w-full rounded-[20px] text-left sm:rounded-[24px] ${
                      isActive
                        ? // Короткая ENTER-анимация 150ms — нет layout-прыжков
                          // от слишком плавного роста ширины.
                          "transition-[width,background-color,box-shadow] duration-150 bg-primary lg:w-[calc(100%+92px)] xl:w-[calc(100%+120px)] shadow-[0_24px_64px_-12px_rgba(0,51,161,0.5)]"
                        : // Inactive: без любых transition или translate hover —
                          // это убирает «прыжки» соседних карточек при наведении.
                          "bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)]"
                    }`}
                  >
                    {/* pl одинаковый везде (заголовки выровнены по x).
                        pr увеличен у активной: компенсирует visible-overlap
                        (60/80px) + комфортный зазор 30-40px до картинки —
                        чтобы текст не заходил под фото. */}
                    <div
                      className={`flex flex-col gap-4 py-4 sm:gap-5 sm:py-5 lg:py-6 pl-6 sm:pl-8 lg:pl-10 xl:pl-[44px] ${
                        isActive
                          ? "pr-6 sm:pr-8 lg:pr-[120px] xl:pr-[160px]"
                          : "pr-6 sm:pr-8 lg:pr-10 xl:pr-[44px]"
                      }`}
                    >
                      <h3
                        className={`text-[16px] font-medium leading-[1.3] tracking-[-0.3px] transition-colors duration-300 sm:text-[18px] lg:text-[20px] xl:text-[22px] xl:leading-[1.3] ${
                          isActive ? "text-white" : "text-text"
                        }`}
                      >
                        {s.title}
                      </h3>
                      {/* Body — короткое раскрытие 200ms без delay, чтобы
                          совпадало по тайму с ростом ширины плашки.
                          При закрытии instant snap (transition отключен). */}
                      <div
                        className={`overflow-hidden ${
                          isActive
                            ? "transition-[max-height,opacity] duration-200 ease-out max-h-[600px] opacity-100 mt-3 sm:mt-4 lg:mt-5"
                            : "max-h-0 opacity-0 mt-0"
                        }`}
                      >
                        <p className="text-[14px] leading-[1.55] tracking-[-0.2px] text-on-blue sm:text-[15px] lg:text-[16px] xl:text-[17px]">
                          {s.body}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* RIGHT: big image. Слоистая схема — все картинки в общем
              контейнере (z-10, выше синего блока), z-index варьируется:
                • active   — z-2, opacity 0→1 с transition (fade-in поверх)
                • previous — z-1, opacity 1 без transition (нижний слой)
                • остальные — z-0, opacity 0 (вне экрана)
              Так новая картинка возникает ПОВЕРХ старой, и обе при switching
              находятся выше синей плашки. */}
          <div className="relative z-10 h-64 overflow-hidden rounded-[24px] sm:h-80 sm:rounded-[30px] lg:order-2 lg:h-[640px] xl:h-[660px]">
            {SERVICES.map((s) => {
              const isActive = s.key === activeKey;
              const isPrevious = s.key === previousKey;
              return (
                <Image
                  key={s.key}
                  src={s.image}
                  alt=""
                  fill
                  priority={s.key === SERVICES[0].key}
                  sizes="(max-width: 1024px) 100vw, 460px"
                  className={`object-cover ${
                    isActive
                      ? "z-[2] opacity-100 transition-opacity duration-200 ease-out"
                      : isPrevious
                      ? "z-[1] opacity-100"
                      : "z-0 opacity-0"
                  }`}
                />
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}
